import { Component, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardMenuService } from '../../services/dashboard-menu.service';
import { MenuGroupUIModel, MenuUIModel } from '../../models/models';
import { AppState } from '../../../models/classes';
import { Store } from '@ngrx/store';
import { SideNavGroup, SideNavItem } from '../side-navbar/side-navbar.models';
import { menuGroupUpdated, menuLoadedState, userIdState, userPermissionLoaded, menuGroupSortOrderUpdated, outdatedBuild } from '../../../store/store.selector';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '../../../components/auth/auth.service';
import { MenuService } from '../../../services/menuService';
import { OutdatedBuildInfoModel, MenuGroupModel } from '../../../models/models';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { InternalPermissionService } from '../../../services/internal-permission.service';
import { InternalTitlePermissionListItem, SingleMenuGroup } from '../../../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/models';
import { UserRecentService } from '../../../services/user-recent.service';
import { DOCUMENT } from '@angular/common';
import { SessionService } from '../../../services/session.service';

@Component({
	selector: 'app-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
	public userName!: string;
	public lastSelectedMenuGroupIndex!: number;

	sidebarExpanded: boolean = true;
	menuGroupModelList: MenuGroupUIModel[] = [];
	selectedMenuGroupModel!: MenuGroupUIModel | null;
	sideNavGroups: SideNavGroup[] = [];

	// Subscriptions
	public menuLoaded$!: Subscription;
	public userAuthenticated$!: Subscription;
	public menuGroupUpdated$!: Subscription;
	public userPermissionLoaded$!: Subscription;
	public menuGroupSortOrderUpdated$!: Subscription;
	public buidIsOutdated$!: Subscription;
	public loginFailed$!: Subscription;
	public menuContainerSpinner: string = 'menuContainerSpinner';
	public menuLoaded!: boolean;

	public pageLevelSpinner: string = 'pageLevelSpinner';
	public componentLoaded!: boolean;
	public buildIsOutdated: boolean = false;
	public apiBuildVersion!: number;

	private destroy$ = new Subject<void>();
	elementIdPrefix: string = 'dashboard-';

	//TODO: Might need later (?)
	dialpadCallNotificationData = {};
	dialPadUserId: string | undefined = undefined;

	constructor(
		private menuService: DashboardMenuService,
		private store: Store<AppState>,
		private spinnerService: NgxSpinnerService,
		private authService: AuthService,
		private internalMenuServices: MenuService,
		private permissionService: InternalPermissionService,
		public userRecentMenu: UserRecentService,
		private renderer: Renderer2,
		@Inject(DOCUMENT) private document: Document,
		private sessionService: SessionService,
		private router: Router
	) {}

	async ngOnInit(): Promise<void> {
		this.lastSelectedMenuGroupIndex = 0;
		this.componentLoaded = false;
		this.spinnerService.show(this.pageLevelSpinner);
		this.initializeSubscriptions();
		this.toggleSideNav();
	}

	debounceAsync(fn: (...args: any[]) => Promise<any>, delay: number) {
		let timer: string | number | NodeJS.Timeout | undefined;
		let pendingPromise: any = null;

		return function (...args: any[]) {
			if (timer) clearTimeout(timer);

			if (pendingPromise) {
				pendingPromise.reject('Debounced');
				pendingPromise = null;
			}

			return new Promise((resolve, reject) => {
				pendingPromise = { resolve, reject };

				timer = setTimeout(async () => {
					try {
						const result = await fn(...args);
						resolve(result);
					} catch (error) {
						reject(error);
					} finally {
						pendingPromise = null;
					}
				}, delay);
			});
		};
	}

	// TODO: MIGHT NOT NEED IT BECAUSE IT'S NOW COMING FROM userInfoService
	// async getDialpadUserId() {
	// 	const response = await lastValueFrom(this.apiServiceV2.getDialPadUserId({ body: { userEmail: this.authService.accountInfo.username } }));

	// 	// console.log('dpuid', response);

	// 	if (!response.isSuccess) return;

	// 	// console.log('dpuid', response?.data?.items?.[0]);

	// 	this.dialpadWebSocketService.setProperty('dpuid', response?.data?.items?.[0]);
	// }

	async initializeSubscriptions() {
		// SUBSCRIBING TO USER UPDATE EVENT POST SUCCESSFUL AUTHENTICATION
		this.userAuthenticated$ = this.store.select(userIdState).subscribe(async (userId) => {
			if (userId) {
				this.menuLoaded = false;
				this.spinnerService.show(this.menuContainerSpinner);
				this.lastSelectedMenuGroupIndex = 0;
				this.authService.emailAddress = userId;
				this.authService.userId = userId;
				await this.initializeComponent();
			}
		});

		// SUBSCRIBING TO USER PERMISSION LOADED
		this.userPermissionLoaded$ = this.store.select(userPermissionLoaded).subscribe((_allPermission: InternalTitlePermissionListItem[] | undefined) => {
			// ONLY LOAD MENUS WHEN USER PERMISSION HAVE BEEN LOADED
			if (_allPermission) {
				this.internalMenuServices.initialize();
			}
		});

		// SUBSCRIBING TO MENU LOADED STATE
		this.menuLoaded$ = this.store.select(menuLoadedState).subscribe(async (menuLoaded) => {
			if (menuLoaded) {
				await this.initializeMenu();
				this.menuLoaded = true;
				this.spinnerService.hide(this.menuContainerSpinner);
			}
		});

		// SUBSCRIBING TO MENU GROUP UPDATED STATE
		this.menuGroupUpdated$ = this.store.select(menuGroupUpdated).subscribe(async (menuGroups: MenuGroupModel[]) => {
			await this.initializeMenu();
		});

		// SUBSCRIBING TO MENU GROUP UPDATED STATE
		this.menuGroupSortOrderUpdated$ = this.store.select(menuGroupSortOrderUpdated).subscribe(async (menuGroups: SingleMenuGroup[]) => {
			this.menuService.updateSortOrder(menuGroups);
			await this.initializeMenu();
		});

		this.buidIsOutdated$ = this.store.select(outdatedBuild).subscribe((fetchedBuildVersion: OutdatedBuildInfoModel | undefined) => {
			if (fetchedBuildVersion != undefined) {
				if (this.apiBuildVersion != fetchedBuildVersion.buildNumber) {
					const divElements = this.document.querySelectorAll('.appLoader');
					divElements.forEach((element: any) => {
						this.renderer.setStyle(element, 'display', 'none');
					});
					this.buildIsOutdated = true;
				}
			}
		});

		// HIDE SPINNER WHEN SESSION GENERATION FAILS (login failed dialog)
		this.loginFailed$ = this.sessionService.loginFailed$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.spinnerService.hide(this.pageLevelSpinner);
		});
	}

	async initializeComponent() {
		this.componentLoaded = true;
		this.spinnerService.hide(this.pageLevelSpinner);
	}

	async initializeMenu() {
		this.menuGroupModelList = await this.menuService.prepareDashboardMenu();
		this.selectedMenuGroupModel = this.menuGroupModelList.length >= 1 ? this.menuGroupModelList[this.lastSelectedMenuGroupIndex] : null;
		this.selectedMenuGroupModel ? (this.selectedMenuGroupModel.selected = true) : false;
		this.sideNavGroups = this.mapMenuToSideNavGroups(this.menuGroupModelList);
	}

	refreshApp() {
		window.location.reload();
	}

	mapMenuToSideNavGroups(menuGroups: MenuGroupUIModel[]): SideNavGroup[] {
		return menuGroups.map((group) => ({
			id: group.name.replace(/\s+/g, '-').toLowerCase(),
			label: group.name,
			icon: group.iconName || undefined,
			items: group.items.map((item) => this.mapMenuUIModelToSideNavItem(item)),
		}));
	}

	private mapMenuUIModelToSideNavItem(item: MenuUIModel): SideNavItem {
		return {
			id: item.urlGUID || item.name.replace(/\s+/g, '-').toLowerCase(),
			label: item.name,
			routerLink: item.isExternalLink ? undefined : item.urlGUID ? `/${item.urlGUID}` : undefined,
			isExternalLink: item.isExternalLink,
			externalLinkURL: item.externalLinkURL,
			isFavorite: item.menuItem?.isFavorite ?? false,
			children: item.items?.length ? item.items.map((child) => this.mapMenuUIModelToSideNavItem(child)) : undefined,
		};
	}

	onSideNavItemSelected(item: SideNavItem): void {
		if (item.isExternalLink && item.externalLinkURL) {
			window.open(item.externalLinkURL, '_blank');
		} else if (item.routerLink) {
			this.router.navigate([item.routerLink]);
		}
	}

	async onSideNavFavoriteToggled(item: SideNavItem): Promise<void> {
		const menuUIModel = this.findMenuUIModelById(item.id);
		if (menuUIModel) {
			await this.markMenuAsFavorite(menuUIModel);
		}
	}

	private findMenuUIModelById(id: string): MenuUIModel | undefined {
		for (const group of this.menuGroupModelList) {
			const found = this.findInItems(group.items, id);
			if (found) return found;
		}
		return undefined;
	}

	private findInItems(items: MenuUIModel[], id: string): MenuUIModel | undefined {
		for (const item of items) {
			if ((item.urlGUID || item.name.replace(/\s+/g, '-').toLowerCase()) === id) {
				return item;
			}
			if (item.items?.length) {
				const found = this.findInItems(item.items, id);
				if (found) return found;
			}
		}
		return undefined;
	}

	/*
		Event Triggered when mouse out from container
	*/
	focusOutFromMenu(event: any) {
		console.log('Focus is called');
		if (this.sidebarExpanded) {
			this.toggleSideNav();
		}
	}

	toggleSideNav() {
		this.sidebarExpanded = !this.sidebarExpanded;
	}

	/**
	 * Toggle isFavorite for a menu
	 * @param menuUIModel
	 */
	async markMenuAsFavorite(menuUIModel: MenuUIModel) {
		if (menuUIModel) {
			this.spinnerService.show(this.pageLevelSpinner);
			menuUIModel.menuItem.isFavorite = menuUIModel.menuItem.isFavorite ? !menuUIModel.menuItem.isFavorite : true;
			await this.menuService.markMenuAsFavorite(menuUIModel.menuItem);
			this.spinnerService.hide(this.pageLevelSpinner);
		}
	}

	expandSideNavar() {
		this.sidebarExpanded = true;
	}

	onMenuItemSelected(allMenuModels: MenuGroupUIModel[], selectedMenuModel: MenuGroupUIModel) {
		const allMenuModelsExceptSelectedOne = allMenuModels.filter((x) => x.name != selectedMenuModel.name);
		allMenuModelsExceptSelectedOne.forEach((x) => (x.selected = false));
		selectedMenuModel.selected = true;
		this.onMenuGroupSelected(selectedMenuModel);
	}

	onMenuGroupSelected(menuGroupModel: MenuGroupUIModel) {
		this.lastSelectedMenuGroupIndex = this.menuGroupModelList.indexOf(menuGroupModel);
		this.selectedMenuGroupModel = menuGroupModel;
		this.sidebarExpanded = false;
		this.expandSideNavar();
	}

	showImmediateChild(data: MenuUIModel) {
		data.childVisible = true;
		data.items.forEach((x) => (x.nestedChild = false));
	}

	hideImmediateChild(data: MenuUIModel) {
		data.childVisible = false;
	}

	onExternalMenuClicked(menuUIModel: MenuUIModel) {
		this.userRecentMenu.addUserRecentMenu(menuUIModel.menuItem);
	}

	ngOnDestroy(): void {
		this.disposeSubscriptions();
	}

	disposeSubscriptions() {
		this.userAuthenticated$.unsubscribe();
		this.userPermissionLoaded$.unsubscribe();
		this.menuLoaded$.unsubscribe();
		this.menuGroupUpdated$.unsubscribe();
		this.menuGroupSortOrderUpdated$.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
		this.buidIsOutdated$.unsubscribe();
		if (this.loginFailed$) this.loginFailed$.unsubscribe();
	}
}
